/*  Shared  */
import { Response } from 'application/Shared/Helpers';
import { debug } from 'application/Shared/Utils';
import { ReportParams } from 'application/Shared/types/reports';

/*  NPM  */
import axios from 'axios';

/*  React  */
import { useCallback, useState } from 'react';

export interface Recipient {
    type: 'user' | 'email';
    value: string;
}

export interface DeleteScheduledReportPayload {
    clientId?: string;
    id: string;
}

export interface PostScheduledReportPayload {
    clientId?: string;
    name: string;
    notes: string;
    params: ReportParams;
    recipients: { type: 'user' | 'email'; value: string }[];
}

/**
 * A React hook to post a scheduled report.
 * @returns {Object} An object containing the post function and a posting state.
 */
export const usePostScheduledReport = () => {
    const [posting, setPosting] = useState(false);
    const { CancelToken } = axios;
    const source = CancelToken.source();

    const postScheduleReport = useCallback(
        async (payload: PostScheduledReportPayload) => {
            try {
                setPosting(true);
                const response = await axios.post('/scheduledReport', payload, {
                    cancelToken: source.token
                });

                if (!Response.hasData(response, { noId: true })) {
                    throw new Error('No delete scheduledReport response.');
                }
                setPosting(false);
                return true;
            } catch (err) {
                debug.e('Error deleting scheduled report', err);
                setPosting(false);
                return false; // success = false
            }
        },
        [source.token]
    );

    return { posting, postScheduleReport };
};

/**
 * A React hook to delete a scheduled report.
 * @returns {Object} An object containing the delete function and a deleting state.
 */
export const useDeleteScheduledReport = () => {
    const [deleting, setDeleting] = useState(false);
    const { CancelToken } = axios;
    const source = CancelToken.source();

    const deleteScheduleReport = useCallback(
        async (payload: DeleteScheduledReportPayload) => {
            try {
                setDeleting(true);
                const response = await axios.delete(
                    `/scheduledReport/${payload.id}`,
                    {
                        cancelToken: source.token,
                        params: { platformTenantId: payload.clientId }
                    }
                );
                if (!Response.hasData(response, { noId: true })) {
                    throw new Error('No delete scheduledReport response.');
                }
                setDeleting(false);
                return true;
            } catch (err) {
                debug.e('Error deleting scheduled report', err);
                setDeleting(false);
                return false; // success = false
            }
        },
        [source.token]
    );

    return { deleteScheduleReport, deleting };
};
